import { z } from "zod";
import { discordSnowflakeSchema, utcTimestampSchema, } from "./guild-settings.js";
import { boundedNfcTextSchema, sanitizedJsonObjectSchema, uuidSchema, } from "./platform-common.js";
export const MAX_GUILD_SOURCES = 10;
export const MAX_GUILD_RSS_SOURCES = 5;
export const MAX_SOURCE_URL_LENGTH = 2_048;
export const guildSourceTypeSchema = z.enum(["rss", "web"]);
export const guildSourceValidationStatusSchema = z.enum([
    "pending",
    "claimed",
    "valid",
    "invalid",
]);
export const sourceValidationErrorCodeSchema = z
    .string()
    .regex(/^[a-z][a-z0-9_]{0,63}$/);
export function canonicalizeGuildSourceUrl(value) {
    if (value.includes("#"))
        throw new Error("Source URL must not contain a fragment");
    const url = new URL(value);
    if (url.protocol !== "https:" ||
        sourceUrlHasUserInfo(value) ||
        url.username !== "" ||
        url.password !== "" ||
        url.hash !== "") {
        throw new Error("Source URL must be credential-free HTTPS without a fragment");
    }
    return url.toString();
}
function sourceUrlHasUserInfo(value) {
    const schemeEnd = value.indexOf(":");
    if (schemeEnd < 0)
        return false;
    const remainder = value.slice(schemeEnd + 1).trimStart();
    if (!/^[\\/]{2}/.test(remainder))
        return false;
    return remainder
        .slice(2)
        .split(/[\\/?#]/, 1)[0]
        .includes("@");
}
export const guildSourceUrlSchema = z
    .string()
    .min(1)
    .max(4_096)
    .transform((value, context) => {
    try {
        return canonicalizeGuildSourceUrl(value);
    }
    catch {
        context.addIssue({ code: "custom", message: "Invalid guild source URL" });
        return z.NEVER;
    }
})
    .pipe(z.string().max(MAX_SOURCE_URL_LENGTH));
const sourceNameSchema = boundedNfcTextSchema(80, 512);
const sourceDescriptionSchema = z
    .union([boundedNfcTextSchema(500, 2_048), z.literal(""), z.null()])
    .transform((value) => (value === "" ? null : value));
export const guildSourceCollectionPathSchema = z
    .object({ guildId: discordSnowflakeSchema })
    .strict();
export const guildSourceItemPathSchema = z
    .object({ guildId: discordSnowflakeSchema, sourceId: uuidSchema })
    .strict();
export const guildSourceListQuerySchema = z.object({}).strict();
export const guildSourceSchema = z
    .object({
    sourceId: uuidSchema,
    guildId: discordSnowflakeSchema,
    sourceType: guildSourceTypeSchema,
    name: sourceNameSchema,
    canonicalUrl: guildSourceUrlSchema,
    description: sourceDescriptionSchema,
    enabled: z.boolean(),
    validationStatus: guildSourceValidationStatusSchema,
    validationErrorCode: sourceValidationErrorCodeSchema.nullable(),
    validatedAt: utcTimestampSchema.nullable(),
    revision: z.int().positive(),
    createdAt: utcTimestampSchema,
    updatedAt: utcTimestampSchema,
})
    .strict();
export const guildSourceCreateSchema = z
    .object({
    sourceType: guildSourceTypeSchema,
    name: sourceNameSchema,
    url: guildSourceUrlSchema,
    description: sourceDescriptionSchema.optional(),
    enabled: z.boolean().optional(),
})
    .strict();
export const guildSourcePatchSchema = z
    .object({
    expectedRevision: z.int().positive(),
    sourceType: guildSourceTypeSchema.optional(),
    name: sourceNameSchema.optional(),
    url: guildSourceUrlSchema.optional(),
    description: sourceDescriptionSchema.optional(),
    enabled: z.boolean().optional(),
})
    .strict()
    .refine((value) => value.sourceType !== undefined ||
    value.name !== undefined ||
    value.url !== undefined ||
    value.description !== undefined ||
    value.enabled !== undefined, "Source patch must contain a change");
export const guildSourceDeleteSchema = z
    .object({ expectedRevision: z.int().positive() })
    .strict();
export const guildSourceListSchema = z
    .object({
    guildId: discordSnowflakeSchema,
    sources: z.array(guildSourceSchema).max(MAX_GUILD_SOURCES),
})
    .strict()
    .superRefine((value, context) => {
    if (new Set(value.sources.map(({ sourceId }) => sourceId)).size !==
        value.sources.length) {
        context.addIssue({
            code: "custom",
            message: "Source IDs must be unique",
        });
    }
    if (new Set(value.sources.map(({ canonicalUrl }) => canonicalUrl)).size !==
        value.sources.length) {
        context.addIssue({
            code: "custom",
            message: "Source URLs must be unique within a guild",
        });
    }
    if (value.sources.filter(({ sourceType }) => sourceType === "rss").length >
        MAX_GUILD_RSS_SOURCES) {
        context.addIssue({
            code: "custom",
            message: `A guild may have at most ${MAX_GUILD_RSS_SOURCES} RSS sources`,
        });
    }
    if (value.sources.some(({ guildId }) => guildId !== value.guildId)) {
        context.addIssue({
            code: "custom",
            message: "Sources must belong to the requested guild",
        });
    }
});
export const guildSourceConflictSchema = z
    .object({
    code: z.literal("guild_source_revision_conflict"),
    message: z.string().trim().min(1).max(500),
    requestId: z.string().trim().min(1).max(128),
    current: guildSourceSchema,
})
    .strict();
export const guildSourceRequestRuntimeSchema = z
    .object({
    sourceId: uuidSchema,
    guildId: discordSnowflakeSchema,
    sourceType: guildSourceTypeSchema,
    name: sourceNameSchema,
    canonicalUrl: guildSourceUrlSchema,
    description: sourceDescriptionSchema,
    revision: z.int().positive(),
})
    .strict();
export const guildSourceRequestRuntimeListSchema = z
    .object({
    guildId: discordSnowflakeSchema,
    sourceCatalogRevision: z.int().positive(),
    sources: z.array(guildSourceRequestRuntimeSchema).max(MAX_GUILD_SOURCES),
})
    .strict()
    .superRefine((value, context) => {
    if (value.sources.some(({ guildId }) => guildId !== value.guildId)) {
        context.addIssue({
            code: "custom",
            message: "Runtime sources must belong to the requested guild",
        });
    }
    if (new Set(value.sources.map(({ sourceId }) => sourceId)).size !==
        value.sources.length) {
        context.addIssue({
            code: "custom",
            message: "Runtime source IDs must be unique",
        });
    }
});
export const sourceValidationClaimSchema = z
    .object({
    sourceId: uuidSchema,
    guildId: discordSnowflakeSchema,
    sourceRevision: z.int().positive(),
    claimOwner: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/),
    claimedAt: utcTimestampSchema,
    leaseExpiresAt: utcTimestampSchema,
    retryCount: z.int().nonnegative(),
})
    .strict()
    .refine((value) => Date.parse(value.leaseExpiresAt) > Date.parse(value.claimedAt), {
    message: "Validation lease expiry must follow claim time",
    path: ["leaseExpiresAt"],
});
const sourceValidationResultShape = {
    sourceId: uuidSchema,
    sourceRevision: z.int().positive(),
    claimOwner: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/),
    status: z.enum(["valid", "invalid"]),
    errorCode: sourceValidationErrorCodeSchema.nullable(),
};
export const sourceValidationCompletionSchema = z
    .object({
    ...sourceValidationResultShape,
})
    .strict()
    .refine((value) => (value.status === "valid") === (value.errorCode === null), {
    message: "Only invalid validation results include an error code",
    path: ["errorCode"],
});
export const sourceValidationResultSchema = z
    .object({
    ...sourceValidationResultShape,
    validatedAt: utcTimestampSchema,
})
    .strict()
    .refine((value) => (value.status === "valid") === (value.errorCode === null), {
    message: "Only invalid validation results include an error code",
    path: ["errorCode"],
});
export const sourceAuditDisplaySchema = z
    .object({
    eventId: uuidSchema,
    guildId: discordSnowflakeSchema,
    sourceId: uuidSchema.nullable(),
    actorUserId: discordSnowflakeSchema.nullable(),
    action: z.string().min(1).max(100),
    revision: z.int().positive(),
    summary: sanitizedJsonObjectSchema(8_192).nullable(),
    errorCode: sourceValidationErrorCodeSchema.nullable(),
    createdAt: utcTimestampSchema,
})
    .strict();
//# sourceMappingURL=guild-sources.js.map